import React, { useEffect, useRef } from "react";
import { FaLeaf, FaRegLightbulb, FaSolarPanel, FaThumbsUp } from "react-icons/fa";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ValuesSection = () => {
    const headingRef = useRef(null);
    const valueRefs = useRef([]);
    const iconRefs = useRef([]);

    useEffect(() => {
        setTimeout(() => {
            ScrollTrigger.refresh();
        }, 500);

        const mm = gsap.matchMedia();

        mm.add(
            {
                isMobile: "(max-width: 768px)",
                isDesktop: "(min-width: 769px)",
            },
            (context) => {
                let { isMobile } = context.conditions;

                // Heading animation
                gsap.fromTo(
                    headingRef.current,
                    { opacity: 0, y: isMobile ? -30 : -50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1.2,
                        ease: "power2.out",

                        scrollTrigger: {
                            trigger: headingRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        },
                    }
                );

                // Value card animation
                gsap.fromTo(
                    valueRefs.current,
                    { opacity: 0, y: isMobile ? 30 : 60, scale: isMobile ? 0.98 : 0.9 },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 1.1,
                        ease: "power3.out",
                        stagger: 0.2,
                        scrollTrigger: {
                            trigger: headingRef.current,
                            start: "top 80%",
                            toggleActions: "play none none reverse"
                        },
                    }
                );

                // Icon animation
                gsap.fromTo(
                    iconRefs.current,
                    { opacity: 0, scale: 0.7, rotation: isMobile ? 0 : -180 },
                    {
                        opacity: 1,
                        scale: 1,
                        rotation: 0,
                        duration: 1.3,
                        ease: isMobile ? "power1.out" : "elastic.out(1, 0.4)",
                        stagger: 0.2,
                        scrollTrigger: {
                            trigger: headingRef.current,
                            start: "top 80%",
                            toggleActions: "play none none reverse"
                        },
                    }
                );
            }
        );

        return () => mm.revert();
    }, []);

    const values = [
        {
            icon: <FaRegLightbulb className="text-yellow-400" />,
            title: "Expertise",
            text:
                "With years of experience in the solar industry, our team possesses the knowledge and skills to deliver exceptional results on every project.",
        },
        {
            icon: <FaSolarPanel className="text-green-400" />,
            title: "Quality Products",
            text:
                "We partner with leading manufacturers to provide high-quality solar panels, inverters, and other components, ensuring reliability and performance.",
        },
        {
            icon: <FaThumbsUp className="text-blue-500" />,
            title: "Customer Satisfaction",
            text:
                "At Sun Pledge Holdings, customer satisfaction is our priority, with superior service and support throughout every project stage.",
        },
        {
            icon: <FaLeaf className="text-green-600" />,
            title: "Sustainability",
            text:
                "Choosing solar energy saves money and helps protect the planet. We are committed to sustainability and reducing carbon emissions.",
        },
    ];

    return (
        <div className="w-full py-16 bg-white flex flex-col items-center justify-center px-6 md:px-12 text-customblue overflow-hidden">
            <div className="text-center mb-14">
                <h1
                    ref={headingRef}
                    className="font-montserrat text-4xl sm:text-5xl md:text-6xl font-bold text-[#031c3f]"
                >
                    OUR VALUES
                </h1>
            </div>

            <div className="max-w-screen-2xl flex flex-wrap justify-center gap-6">
                {values.map((val, i) => (
                    <div
                        key={i}
                        ref={(el) => (valueRefs.current[i] = el)}
                        className="value-item transition-transform transform hover:scale-105 bg-white shadow-lg rounded-2xl p-6 sm:p-8 w-full sm:w-[45%] md:w-[22%] text-center flex flex-col items-center"
                    >

                        <div ref={(el) => (iconRefs.current[i] = el)} className="value-icon text-6xl lg:text-7xl mb-4">
                            {val.icon}
                        </div>
                        <h3 className="font-montserrat text-xl mb-4 sm:text-2xl font-bold">{val.title}</h3>
                        <p className="font-roboto text-sm sm:text-base  leading-relaxed">{val.text}</p>

                    </div>

                ))}
            </div>
        </div>
    );
};

export default ValuesSection;
